import React, { useState, useRef, useCallback } from 'react';
import {
  GripVertical,
  Trash2,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { ExerciseInstance, LoadType } from '../types';
import { MOVEMENT_PATTERN_LABELS, MUSCLE_GROUP_LABELS } from '../types';
import { formatRest } from '../utils/helpers';

interface Props {
  exercise: ExerciseInstance;
  index: number;
  onUpdate: (updates: Partial<ExerciseInstance>) => void;
  onRemove: () => void;
}

export default function ExerciseRow({ exercise, index, onUpdate, onRemove }: Props) {
  const [expanded, setExpanded] = useState(false);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: exercise.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const tagColor = exercise.tag === 'compound'
    ? 'bg-blue-100 dark:bg-blue-950 text-blue-700 dark:text-blue-300'
    : exercise.tag === 'isolation'
    ? 'bg-purple-100 dark:bg-purple-950 text-purple-700 dark:text-purple-300'
    : exercise.tag === 'warmup'
    ? 'bg-orange-100 dark:bg-orange-950 text-orange-700 dark:text-orange-300'
    : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300';

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`group border border-slate-100 dark:border-slate-700 rounded-lg transition-all ${
        expanded ? 'bg-white dark:bg-slate-800 shadow-sm' : 'bg-slate-50/50 dark:bg-slate-800/50 hover:bg-white dark:hover:bg-slate-800'
      }`}
    >
      {/* ── Main Row ─────────────────────── */}
      <div className="flex items-center gap-1.5 px-2 py-1.5">
        {/* Drag handle */}
        <button
          {...attributes}
          {...listeners}
          className="p-0.5 text-slate-300 dark:text-slate-600 hover:text-slate-500 cursor-grab active:cursor-grabbing"
        >
          <GripVertical size={14} />
        </button>

        {/* Index */}
        <span className="text-[10px] font-bold text-slate-400 w-4 text-center">{index + 1}</span>

        {/* Name */}
        <span
          className="font-medium text-xs flex-1 min-w-0 whitespace-normal break-words leading-tight cursor-pointer"
          onClick={() => setExpanded(!expanded)}
          title={exercise.name}
        >
          {exercise.name}
        </span>

        {/* Inline fields ── Sets × Reps */}
        <InlineInput
          value={String(exercise.sets)}
          onChange={(v) => onUpdate({ sets: parseInt(v) || 1 })}
          width="w-8"
          label="serie"
          type="number"
        />
        <span className="text-[10px] text-slate-400">×</span>
        <InlineInput
          value={exercise.reps}
          onChange={(v) => onUpdate({ reps: v })}
          width="w-12"
          label="powt"
        />

        {/* Load */}
        <div className="flex items-center gap-0.5">
          <InlineInput
            value={String(exercise.load)}
            onChange={(v) => onUpdate({ load: parseFloat(v) || 0 })}
            width="w-10"
            label="obciążenie"
            type="number"
          />
          <select
            value={exercise.loadType}
            onChange={(e) => onUpdate({ loadType: e.target.value as LoadType })}
            className="text-[10px] bg-transparent border-none p-0 text-slate-400 focus:outline-none cursor-pointer"
          >
            <option value="rpe">RPE</option>
            <option value="percent_1rm">%1RM</option>
          </select>
        </div>

        {/* Tag */}
        <span className={`text-[9px] px-1.5 py-0.5 rounded font-bold uppercase ${tagColor}`}>
          {exercise.tag.charAt(0)}
        </span>

        {/* Expand / Delete */}
        <button
          onClick={() => setExpanded(!expanded)}
          className="p-0.5 text-slate-300 hover:text-slate-500 transition"
        >
          {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </button>
        <button
          onClick={onRemove}
          className="p-0.5 text-slate-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition"
        >
          <Trash2 size={14} />
        </button>
      </div>

      {/* ── Expanded Details ─────────────── */}
      {expanded && (
        <div className="px-3 pb-3 pt-1 border-t border-slate-100 dark:border-slate-700 animate-slide-down">
          <div className="grid grid-cols-3 gap-3 text-xs">
            {/* Tempo */}
            <div>
              <label className="block text-[10px] font-medium text-slate-400 mb-0.5">Tempo</label>
              <input
                type="text"
                value={exercise.tempo}
                onChange={(e) => onUpdate({ tempo: e.target.value })}
                className="w-full px-2 py-1 rounded border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-xs focus:outline-none focus:ring-1 focus:ring-brand-500"
              />
            </div>

            {/* Rest */}
            <div>
              <label className="block text-[10px] font-medium text-slate-400 mb-0.5">
                Odpoczynek (s)
              </label>
              <input
                type="number"
                min={0}
                step={15}
                value={exercise.rest}
                onChange={(e) => onUpdate({ rest: parseInt(e.target.value) || 0 })}
                className="w-full px-2 py-1 rounded border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-xs focus:outline-none focus:ring-1 focus:ring-brand-500"
              />
            </div>

            {/* Movement pattern */}
            <div>
              <label className="block text-[10px] font-medium text-slate-400 mb-0.5">
                Wzorzec
              </label>
              <div className="px-2 py-1 rounded bg-slate-50 dark:bg-slate-700 text-[10px] font-medium text-slate-600 dark:text-slate-300">
                {MOVEMENT_PATTERN_LABELS[exercise.movementPattern]}
              </div>
            </div>
          </div>

          {/* Muscles */}
          <div className="mt-2">
            <label className="block text-[10px] font-medium text-slate-400 mb-1">
              Mięśnie
            </label>
            <div className="flex flex-wrap gap-1">
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-100 dark:bg-green-950 text-green-700 dark:text-green-300 font-semibold">
                {MUSCLE_GROUP_LABELS[exercise.primaryMuscle]}
              </span>
              {exercise.secondaryMuscles.map((m) => (
                <span
                  key={m}
                  className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400 font-medium"
                >
                  {MUSCLE_GROUP_LABELS[m]}
                </span>
              ))}
            </div>
          </div>

          {/* Notes */}
          <div className="mt-2">
            <label className="block text-[10px] font-medium text-slate-400 mb-0.5">Notatki</label>
            <input
              type="text"
              value={exercise.notes}
              onChange={(e) => onUpdate({ notes: e.target.value })}
              placeholder="Dodaj notatki..."
              className="w-full px-2 py-1 rounded border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-xs focus:outline-none focus:ring-1 focus:ring-brand-500 placeholder:text-slate-300"
            />
          </div>
        </div>
      )}
    </div>
  );
}

// ── Tiny inline input ───────────────────────

function InlineInput({
  value,
  onChange,
  width = 'w-12',
  label,
  type = 'text',
}: {
  value: string;
  onChange: (v: string) => void;
  width?: string;
  label?: string;
  type?: string;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);

  const commit = useCallback(() => {
    setEditing(false);
    if (draft !== value) onChange(draft);
  }, [draft, value, onChange]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      commit();
    } else if (e.key === 'Escape') {
      setDraft(value);
      setEditing(false);
    } else if (e.key === 'Tab') {
      commit();
    }
  };

  if (editing) {
    return (
      <input
        ref={inputRef}
        type={type}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={handleKeyDown}
        autoFocus
        className={`${width} px-1 py-0.5 rounded border border-brand-400 bg-white dark:bg-slate-700 text-xs text-center focus:outline-none focus:ring-1 focus:ring-brand-500`}
      />
    );
  }

  return (
    <button
      onClick={() => {
        setDraft(value);
        setEditing(true);
      }}
      className={`${width} px-1 py-0.5 rounded text-xs text-center font-mono font-medium bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 transition cursor-text`}
      title={label}
    >
      {value}
    </button>
  );
}
